import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Skeleton
} from '@mui/material';
import { Assessment, ArrowBackIos, ArrowForwardIos, Refresh } from '@mui/icons-material';
import { groupsApi, performanceApi } from './api';
import PerformanceTypeDialog from './PerformanceTypeDialog';
import { useSchoolFeesMode } from './SchoolFeesContext';

const GroupPerformance = ({ group }) => {
  const navigate = useNavigate();
  const { getModeForGroup } = useSchoolFeesMode();
  
  // State management
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0);
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);

  // State for available periods from backend
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState(null);

  // Fetch available periods from backend
  const fetchAvailablePeriods = async () => {
    if (!group?.id) return;
    
    setLoadingPeriods(true);
    try {
      // First get current period
      const currentResponse = await groupsApi.getGroupCurrentSummary(group.id);
      
      // Fix: Use the correct key from API response
      const summaryData = currentResponse?.current_period_summary || currentResponse?.financial_summary;
      
      if (summaryData) {
        const currentPeriod = {
          periodStart: summaryData.period_start,
          periodEnd: summaryData.period_end,
          displayMonth: formatPeriodDisplay(
            summaryData.period_start,
            summaryData.period_end
          ),
          isCurrent: true
        };

        // Start with current period, then try to get historical periods
        let allPeriods = [currentPeriod];
        
        // Try to get historical periods - will gracefully handle null/empty responses
        try {
          const periodsResponse = await groupsApi.getGroupPeriods(group.id);
          if (periodsResponse?.periods && Array.isArray(periodsResponse.periods) && periodsResponse.periods.length > 0) {
            // If we have historical periods, use them
            allPeriods = periodsResponse.periods.map((period, index) => ({
              periodStart: period.period_start,
              periodEnd: period.period_end,
              displayMonth: formatPeriodDisplay(period.period_start, period.period_end),
              isCurrent: index === 0 // Assuming first period is current
            }));
          }
          // If periodsResponse is null or empty, we'll just use the current period
        } catch (periodsErr) {
          // Silently handle the case where periods endpoint doesn't exist or returns null
          console.log('Historical periods not available yet, using current period only');
        }
        
        setAvailablePeriods(allPeriods);
      }
    } catch (err) {
      console.error('Error fetching available periods:', err);
      setError('Failed to fetch available periods');
    } finally {
      setLoadingPeriods(false);
    }
  };

  // Helper function to format period display
  const formatPeriodDisplay = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Current Period';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
    }
    
    return `${start.toLocaleDateString('en-US', { month: 'short' })} - ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
  };

  const currentPeriod = availablePeriods[currentPeriodIndex];

  // Fetch financial data for the current period
  const fetchFinancialData = async (periodStart = null, periodEnd = null) => {
    if (!group?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let response;
      if (currentPeriod?.isCurrent && !periodStart && !periodEnd) {
        // Use current summary endpoint for current period
        response = await groupsApi.getGroupCurrentSummary(group.id);
        
        // Normalize the response structure to match expected format
        if (response?.current_period_summary) {
          response = {
            financial_summary: response.current_period_summary,
            group_info: response.group_info
          };
        }
      } else {
        // Use financial summary with period parameters for historical data
        response = await groupsApi.getGroupFinancialSummary(
          group.id, 
          periodStart, 
          periodEnd
        );
      }
      setFinancialData(response);
    } catch (err) {
      console.error('Error fetching financial data:', err);
      setError(err.message || 'Failed to fetch financial data');
    } finally {
      setLoading(false);
    }
  };

  // Load available periods on component mount
  useEffect(() => {
    if (group?.id) {
      fetchAvailablePeriods();
    }
  }, [group?.id]);

  // Load period data when period changes
  useEffect(() => {
    if (group?.id && currentPeriod) {
      fetchFinancialData(currentPeriod.periodStart, currentPeriod.periodEnd);
    }
  }, [group?.id, currentPeriodIndex, availablePeriods]);

  // Handle period navigation
  const handlePreviousMonth = () => {
    setCurrentPeriodIndex(prev => 
      prev < availablePeriods.length - 1 ? prev + 1 : prev
    );
  };

  const handleNextMonth = () => {
    setCurrentPeriodIndex(prev => 
      prev > 0 ? prev - 1 : prev
    );
  };

  // Generate/Update performance analysis
  const handlePerformanceAnalysis = async (isSchoolFees = false) => {
    if (!group?.id) return;
    
    setGeneratingAnalysis(true);
    
    try {
      // ✅ Mode is already stored in context by PerformanceTypeDialog
      // Just use it here
      console.log('🚀 Generating with mode:', isSchoolFees);
      
      // First try to generate analysis with the selected type
      await performanceApi.generatePerformanceAnalysis(group.id, isSchoolFees);
      
      // Then navigate to detailed view
      navigate(`/management/group-performance/${group.id}`);
    } catch (err) {
      console.error('Error generating performance analysis:', err);
      
      // If generation fails, try to update existing analysis
      try {
        await performanceApi.updatePerformanceAnalysis(
          group.id, 
          {
            period_start: currentPeriod.periodStart,
            period_end: currentPeriod.periodEnd
          },
          isSchoolFees
        );
        navigate(`/management/group-performance/${group.id}`);
      } catch (updateErr) {
        console.error('Error updating performance analysis:', updateErr);
        setError('Failed to generate performance analysis');
      }
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  const handleTypeSelection = (isSchoolFees) => {
    handlePerformanceAnalysis(isSchoolFees);
  };

  // Refresh current period data
  const handleRefresh = () => {
    if (currentPeriod) {
      fetchFinancialData(currentPeriod.periodStart, currentPeriod.periodEnd);
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  // Helper function to get color based on value type
  const getValueColor = (type) => {
    switch (type) {
      case 'positive':
        return '#4caf50'; // Green for positive values
      case 'negative':
        return '#f44336'; // Red for negative values
      case 'neutral':
        return '#2196f3'; // Blue for neutral values
      case 'warning':
        return '#ff9800'; // Orange for warning values
      default:
        return '#333';
    }
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <Grid container spacing={3}>
      {[1, 2, 3].map((item) => (
        <Grid item xs={12} md={item === 3 ? 12 : 6} key={item}>
          <Card variant="outlined">
            <CardContent>
              <Skeleton variant="text" width="40%" height={32} />
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((subItem) => (
                  <Grid item xs={6} key={subItem}>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="60%" height={28} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  if (!group?.id) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="warning">No group selected</Alert>
      </Paper>
    );
  }

  const summary = financialData?.financial_summary || {};
  const groupInfo = financialData?.group_info || {};

  return (
    <Paper sx={{ p: 0, mb: 3, overflow: 'hidden' }}>
      <Box sx={{ p: 3 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Performance Summary
            </Typography>
            
            {/* Period Navigation - Only show if more than one period available */}
            {availablePeriods.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton 
                  onClick={handlePreviousMonth}
                  disabled={currentPeriodIndex >= availablePeriods.length - 1 || loading || loadingPeriods}
                  size="small"
                >
                  <ArrowBackIos fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: '150px', textAlign: 'center' }}>
                  {currentPeriod?.displayMonth || 'Loading...'}
                  {currentPeriod?.isCurrent && ' (Current)'}
                </Typography>
                <IconButton 
                  onClick={handleNextMonth}
                  disabled={currentPeriodIndex <= 0 || loading || loadingPeriods}
                  size="small"
                >
                  <ArrowForwardIos fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Current Period Display (when only one period) */}
            {availablePeriods.length === 1 && (
              <Typography variant="body2" sx={{ 
                px: 2, 
                py: 0.5, 
                bgcolor: 'primary.light', 
                color: 'primary.contrastText',
                borderRadius: 1,
                fontSize: '0.75rem'
              }}>
                {currentPeriod?.displayMonth || 'Current Period'}
              </Typography>
            )}

            {/* Refresh Button */}
            <IconButton onClick={handleRefresh} disabled={loading} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleRefresh}
              disabled={loading}
              size="small"
            >
              Refresh Data
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={generatingAnalysis ? <CircularProgress size={16} /> : <Assessment />}
              onClick={() => setTypeDialogOpen(true)}  // CHANGED THIS LINE
              disabled={generatingAnalysis || loading}
            >
              {generatingAnalysis ? 'Generating...' : 'View Performance Analysis'}
            </Button>
          </Box>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* No Data Available */}
        {!loading && !financialData && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No financial data available for the selected period.
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <LoadingSkeleton />
        ) : (
          financialData && (
            <Grid container spacing={3}>
              {/* Loans Summary Card */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Loans Summary
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Long Term Loans
                        </Typography>
                        <Typography variant="h6" sx={{ color: getValueColor('neutral') }}>
                          {formatCurrency(summary.total_longterm_loans)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Advance Balance
                        </Typography>
                        <Typography variant="h6" sx={{ color: getValueColor('warning') }}>
                          {formatCurrency(summary.total_advance_balance)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Loan Repayments
                        </Typography>
                        <Typography variant="h6" sx={{ color: getValueColor('positive') }}>
                          {formatCurrency(summary.total_loan_repayments)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Loans Given This Period
                        </Typography>
                        <Typography variant="h6" sx={{ color: getValueColor('neutral') }}>
                          {formatCurrency(summary.loans_given_this_period)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Savings Summary Card */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Savings Summary
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Savings
                        </Typography>
                        <Typography variant="h6" sx={{ color: getValueColor('positive') }}>
                          {formatCurrency(summary.total_savings)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Monthly Savings
                        </Typography>
                        <Typography variant="h6" sx={{ color: getValueColor('positive') }}>
                          {formatCurrency(summary.monthly_savings)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Interest Paid
                        </Typography>
                        <Typography variant="h6" sx={{ color: getValueColor('positive') }}>
                          {formatCurrency(summary.total_interest_paid)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Advance Payments
                        </Typography>
                        <Typography variant="h6" sx={{ color: getValueColor('warning') }}>
                          {formatCurrency(summary.total_advance_payments)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )
        )}

        {/* Period Information */}
        {currentPeriod && !loading && financialData && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Showing data for period: {currentPeriod.periodStart} to {currentPeriod.periodEnd}
              {groupInfo.name && ` • Group: ${groupInfo.name}`}
              {groupInfo.location && ` • Location: ${groupInfo.location}`}
              {availablePeriods.length === 1 && (
                <Typography component="span" sx={{ 
                  ml: 1, 
                  px: 1, 
                  py: 0.25, 
                  bgcolor: 'info.light', 
                  borderRadius: 0.5,
                  fontSize: '0.625rem'
                }}>
                  More periods will be available as data accumulates
                </Typography>
              )}
            </Typography>
          </Box>
        )}
      </Box>
      {/* Performance Type Selection Dialog */}
    <PerformanceTypeDialog
      open={typeDialogOpen}
      onClose={() => setTypeDialogOpen(false)}
      onSelect={handleTypeSelection}
      loading={generatingAnalysis}
      groupId={group?.id}
    />
    </Paper>
  );
};

export default GroupPerformance;